import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Volume2, X, Loader2, Languages } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { speak, stopSpeaking, speakBoth } from "@/lib/buddy";

const ZONE = "[data-translate]";
const SKIP = "button, a, input, textarea, select, [contenteditable='true'], [data-no-translate]";

const wordAt = (x, y) => {
  let node, off;
  if (document.caretPositionFromPoint) { const p = document.caretPositionFromPoint(x, y); node = p?.offsetNode; off = p?.offset; }
  else if (document.caretRangeFromPoint) { const r = document.caretRangeFromPoint(x, y); node = r?.startContainer; off = r?.startOffset; }
  if (!node || node.nodeType !== 3) return null;
  const t = node.textContent;
  let a = off, b = off;
  while (a > 0 && /[\p{L}'’-]/u.test(t[a - 1])) a--;
  while (b < t.length && /[\p{L}'’-]/u.test(t[b])) b++;
  const word = t.slice(a, b).replace(/^['’-]+|['’-]+$/g, "");
  return word.length > 0 ? { word, context: (node.parentElement.closest("p, li, h1, h2, h3, h4, blockquote, td") || node.parentElement).textContent.slice(0, 500) } : null;
};

const place = (pos, w = 300, h = 220) => ({ left: Math.max(12, Math.min(pos.x - w / 2, window.innerWidth - w - 12)), top: pos.y + h > window.innerHeight ? Math.max(12, pos.y - h - 12) : pos.y + 14 });

const Card = ({ st, onClose, voice }) => {
  const say = () => st.data && (st.data.kind === "word" ? speakBoth(null, st.data.es, voice) : speak(st.data.es, voice, 1, "es")).catch(() => {});
  const { left, top } = place(st.pos);
  return createPortal(
    <div className="fixed inset-0 z-[75]" onPointerDown={onClose} data-testid="es-backdrop">
      <div style={{ left, top }} onPointerDown={(e) => e.stopPropagation()} className="absolute w-[300px] max-h-[60vh] overflow-y-auto rounded-3xl bg-[#FDFBF7] border border-[#E6DEC3] shadow-2xl p-5 rise" data-testid="es-card">
        <div className="flex items-start gap-2">
          <p className="text-[10px] uppercase tracking-widest text-[#C87D53] font-semibold flex-1 pt-1">Español</p>
          {st.data && <button onClick={say} data-testid="es-say" aria-label="Escuchar" className="p-2 rounded-full bg-[#F1EADB] hover:bg-[#E9DFC9] transition-colors"><Volume2 className="w-4 h-4" /></button>}
          <button onClick={onClose} data-testid="es-close" aria-label="Close" className="p-2 rounded-full hover:bg-[#F1EADB] transition-colors"><X className="w-4 h-4" /></button>
        </div>
        {st.loading && <p className="text-sm text-stone-500 mt-1 inline-flex items-center gap-2" data-testid="es-loading"><Loader2 className="w-4 h-4 animate-spin" /> Traduciendo…</p>}
        {st.error && <p className="text-sm text-[#C87D53] mt-1" data-testid="es-error">{st.error}</p>}
        {st.data && <>
          <p className={`font-display text-[#2C4A3E] mt-1 ${st.data.kind === "word" ? "text-3xl" : "text-lg leading-snug"}`} data-testid="es-text">{st.data.es}</p>
          <p className="text-xs text-stone-500 mt-1 line-clamp-3" data-testid="es-source">{st.data.text}</p>
          {st.data.meaning && <p className="text-sm text-stone-700 mt-3 rounded-xl bg-[#F7F3EB] px-3 py-2" data-testid="es-meaning">{st.data.meaning}</p>}
        </>}
      </div>
    </div>, document.body);
};

export const SpanishTap = () => {
  const { profile, buddy } = useProfile();
  const [st, setSt] = useState(null);
  const [chip, setChip] = useState(null);
  const timer = useRef(null);
  const on = profile?.role === "student" && !!profile.spanish_help;

  const run = useCallback(async (text, context, pos) => {
    setChip(null);
    stopSpeaking();
    setSt({ pos, loading: true });
    try { const r = await http.post("/translate", { text: text.slice(0, 600), context }); setSt((s) => s && { ...s, loading: false, data: r.data }); }
    catch (e) { setSt((s) => s && { ...s, loading: false, error: formatApiError(e) }); }
  }, []);

  useEffect(() => { document.body.classList.toggle("es-on", on); return () => document.body.classList.remove("es-on"); }, [on]);

  useEffect(() => {
    if (!on) return;
    const click = (e) => {
      const zone = e.target.closest?.(ZONE);
      if (!zone || e.target.closest(SKIP)) return;
      const sel = window.getSelection();
      if (sel && !sel.isCollapsed && sel.toString().trim()) return;
      const hit = wordAt(e.clientX, e.clientY);
      if (hit) run(hit.word, hit.context, { x: e.clientX, y: e.clientY });
    };
    const selChange = () => {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        const sel = window.getSelection();
        const text = sel?.toString().trim();
        if (!text || text.split(/\s+/).length < 2 || !sel.rangeCount) return setChip(null);
        const anchor = sel.anchorNode?.nodeType === 3 ? sel.anchorNode.parentElement : sel.anchorNode;
        if (!anchor?.closest?.(ZONE)) return setChip(null);
        const r = sel.getRangeAt(0).getBoundingClientRect();
        setChip({ text, pos: { x: r.left + r.width / 2, y: r.bottom } });
      }, 450);
    };
    document.addEventListener("click", click);
    document.addEventListener("selectionchange", selChange);
    return () => { clearTimeout(timer.current); document.removeEventListener("click", click); document.removeEventListener("selectionchange", selChange); };
  }, [on, run]);

  useEffect(() => {
    if (!st) return;
    const esc = (e) => e.key === "Escape" && setSt(null);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [st]);

  if (!on) return null;
  return <>
    {chip && createPortal(
      <button style={{ left: Math.max(12, Math.min(chip.pos.x - 60, window.innerWidth - 132)), top: Math.min(chip.pos.y + 10, window.innerHeight - 56) }}
        onPointerDown={(e) => e.preventDefault()} onClick={() => run(chip.text, "", chip.pos)} data-testid="es-translate-selection"
        className="fixed z-[74] inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2C4A3E] text-white text-sm shadow-lg hover:bg-[#1F3D33] transition-colors">
        <Languages className="w-4 h-4" />Traducir</button>, document.body)}
    {st && <Card st={st} voice={buddy?.voice} onClose={() => setSt(null)} />}
  </>;
};

export const SpanishSwitch = () => {
  const { profile, setSpanish } = useProfile();
  const on = !!profile?.spanish_help;
  return (
    <button onClick={() => setSpanish(!on)} role="switch" aria-checked={on} data-testid="nav-spanish-toggle" title="Tap a word to see it in Spanish"
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border transition-colors ${on ? "bg-[#C87D53] border-[#C87D53] text-white" : "border-[#E6DEC3] text-[#2C4A3E] hover:bg-[#F1EADB]"}`}>
      <Languages className="w-3.5 h-3.5" />Español{on ? " ✓" : ""}
    </button>
  );
};
